<template>
  <div class="not-found">
    <div class="not-found-card">
      <p class="not-found-eyebrow">{{ content.eyebrow }}</p>
      <div class="not-found-code">
        <span class="not-found-digit">4</span>
        <span class="not-found-zero">0</span>
        <span class="not-found-digit">4</span>
      </div>
      <h2 class="not-found-title">{{ content.title }}</h2>
      <p class="not-found-description">{{ content.description }}</p>
      <div class="not-found-actions">
        <router-link class="not-found-button primary" :to="homeAction.to">
          {{ homeAction.label }}
        </router-link>
        <button type="button" class="not-found-button" @click="handleBack">
          {{ backAction.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getLocaleBlock } from '@/utils/i18n';

export default {
  name: 'NotFoundView',
  computed: {
    content() {
      return getLocaleBlock(this.$i18n, 'notFound', {
        eyebrow: 'Error',
        title: 'Page Not Found',
        description: "The page you're looking for doesn't seem to exist.",
        actions: {},
      });
    },
    homeAction() {
      const label =
        this.content?.actions?.home ||
        getLocaleBlock(this.$i18n, 'layout.nav.home', 'Back to Home');
      return {
        label,
        to: { name: 'Home' },
      };
    },
    backAction() {
      return {
        label: this.content?.actions?.back || 'Go Back',
      };
    },
  },
  methods: {
    handleBack() {
      if (window?.history?.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.replace({ name: 'Home' });
    },
  },
};
</script>

<style scoped>
.not-found {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--spacing-xl) var(--spacing-md);
  min-height: 60vh;
}

.not-found-card {
  width: 100%;
  max-width: 520px;
  background: var(--surface-color);
  padding: 2.5rem;
  border-radius: 28px;
  box-shadow: 0 24px 60px rgba(18, 32, 62, 0.12);
  border: 1px solid rgba(32, 58, 110, 0.08);
}

.not-found-eyebrow {
  color: var(--primary-color);
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: var(--spacing-sm);
}

.not-found-code {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  font-weight: var(--font-weight-bold);
  font-size: clamp(4rem, 15vw, 6rem);
  line-height: 1;
  color: var(--text-color);
  margin-block: 1rem;
}

.not-found-zero {
  color: var(--primary-color);
  text-shadow: 0 0 24px var(--primary-color-light);
}

.not-found-title {
  color: var(--text-color);
  font-size: clamp(1.5rem, 5vw, 2rem);
  margin-bottom: var(--spacing-sm);
}

.not-found-description {
  color: var(--text-color-light);
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
  margin-bottom: var(--spacing-lg);
  max-width: 380px;
  margin-left: auto;
  margin-right: auto;
}

.not-found-actions {
  display: flex;
  justify-content: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
}

.not-found-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 999px;
  font-weight: var(--font-weight-bold);
  text-decoration: none;
  border: 1px solid var(--border-color);
  background: var(--surface-color);
  color: var(--text-color);
  cursor: pointer;
  transition: all 0.2s ease;
}

.not-found-button:hover {
  transform: translateY(-2px);
  box-shadow: var(--box-shadow-lg);
  border-color: var(--border-color-hover);
}

.not-found-button.primary {
  background: var(--primary-color);
  color: var(--text-color-on-primary);
  border-color: transparent;
}

.not-found-button.primary:hover {
  background: var(--primary-color-dark);
}
</style>
